import { FC, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import IconTrash from 'src/presentations/components/atom/icons/Trash';
import IconNewEdit from 'src/presentations/components/atom/icons/newedit.svg';
import { Box, IconButton, Tooltip } from '@mui/material';
import { MRT_Row, MRT_RowData, useMaterialReactTable } from 'material-react-table';

import { useAddRateCardsToContractFlow, useRemoveRateCardsToContractFlow } from 'src/use-cases/serviceProvider/useServiceProvider';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { useClipboard } from './useClipboard';

import { ITableBaseProps } from './types';

// Custom styled component to remove border radius and border from table container
const NoRadiusTableContainer = styled('div')(`
  height: 100%;
  & .MuiPaper-root.MuiTableContainer-root {
    border-radius: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }
`);

// Define firstCellStyles for consistent styling of the first column
const firstCellStyles = {
  boxShadow: '4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid transparent',
  width: '120px',
  padding: '0 10px 0 10px !important',

  '& .MuiBox-root': {
    gap: '0!important',
    width: '90px',
    justifyContent: 'center',

    '& svg': {
      width: '20px!important',
    },
  },
};

export const RateCardChargesTableBase: FC<ITableBaseProps> = (props) => {
  const { chargeCategory, tableData, columns, contractFlow, setValidationErrors, onUpsertCharge, onValidateCharge, tableRef, sorting, isAdmin } = props;

  const [isSaving, setIsSaving] = useState(false);
  const [rowSelection, setRowSelection] = useState<
    Record<string, [boolean, number, { id: string; contractChargeUnit: 'SHIPMENT' | 'DECLARATION' | 'CONTAINER' | 'WEIGHT' | 'PERCENTAGE' }]>
  >({});

  const { mutateAsync: removeRateCardCharges } = useRemoveRateCardsToContractFlow();
  const { mutateAsync: addRateCardCharges } = useAddRateCardsToContractFlow();
  const { copiedCharges, setCopiedCharges } = useClipboard();

  const openDeleteConfirmModal = async (row: MRT_Row<MRT_RowData>) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure to delete this rate card charge?')) {
      try {
        await removeRateCardCharges({
          contractFlowId: contractFlow?.id,
          data: [
            {
              id: row.original.id,
              contractChargeUnit: row.original.chargeType,
            },
          ],
        });

        openToast({
          toastType: 'success',
          title: 'Success',
          description: 'Rate card charge deleted!',
        });
      } catch (error) {
        openToastError(new Error('Rate card charge delete failed'));
      }
    }
  };

  const handleUpsertCharge = async ({ row, values }) => {
    const newValidationErrors = onValidateCharge(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    setIsSaving(true);
    try {
      await onUpsertCharge(row, values);
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Rate card charge upserted!',
      });
    } catch (error) {
      openToastError(new Error('Rate card charge upsert failed'));
    } finally {
      setIsSaving(false);
      table.setCreatingRow(null);
      table.setEditingRow(null);
    }
  };

  // Custom styles for table cells
  const tableCellStyles = {
    borderBottom: '1px solid #E5E7EB !important',
  };

  const table = useMaterialReactTable({
    enableStickyHeader: true,
    enableSorting: true,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: isAdmin,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    // onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleUpsertCharge,
    // onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleUpsertCharge,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableColumnPinning: true,
    positionActionsColumn: 'first',
    initialState: {
      columnPinning: { left: ['mrt-row-actions'] },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '', // Remove 'Actions' text
        size: 120,
      },
    },
    muiTableHeadProps: {
      sx: {
        ...(isAdmin && { '& th:first-child': firstCellStyles }),
      },
    },
    muiTableBodyProps: {
      sx: {
        ...(isAdmin && { '& td:first-child': firstCellStyles }),
      },
    },
    muiTableProps: {
      sx: {
        borderCollapse: 'separate',
        minWidth: '100%',
      },
    },
    // Apply to both the paper component and table container to ensure border radius is removed
    muiTablePaperProps: {
      sx: {
        borderRadius: '0 !important',
        overflow: 'hidden',
      },
    },
    muiTableContainerProps: {
      sx: {
        borderRadius: '0 !important',
        '& .MuiPaper-root': {
          borderRadius: '0 !important',
        },
      },
      // Apply direct class name style to override Material-UI defaults
      className: 'no-border-radius',
    },
    // Apply border to all table cells
    muiTableBodyCellProps: {
      sx: tableCellStyles,
    },
    muiTableHeadCellProps: {
      sx: tableCellStyles,
    },
    data: tableData,
    columns,
    state: sorting ? { sorting, isSaving } : { isSaving },
    renderRowActions: isAdmin
      ? ({ row, table }) => (
          <Box sx={{ display: 'flex' }}>
            <Tooltip title='Edit'>
              <IconButton onClick={() => table.setEditingRow(row)}>
                <img src={IconNewEdit} alt='Edit' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete'>
              <IconButton color='error' onClick={() => openDeleteConfirmModal(row)}>
                <IconTrash />
              </IconButton>
            </Tooltip>
          </Box>
        )
      : undefined,
    muiTableBodyRowProps: ({ row, table }) => ({
      onClick: (event) => {
        const addSelection = (rowId: string, state: boolean) => {
          if (rowId === 'mrt-row-create') {
            return;
          }
          setRowSelection((prev) => {
            const newIndex =
              (Object.values(prev)
                .map((value) => value[1])
                .sort()
                .slice(-1)[0] || -1) + 1;
            const originalRowData = table.getRow(rowId).original;
            return { ...prev, [rowId]: [state, newIndex, { id: originalRowData.id, contractChargeUnit: originalRowData.chargeType }] };
          });
        };
        if (event.ctrlKey || event.metaKey) {
          addSelection(row.id, !rowSelection[row.id]?.[0]);
        } else if (event.shiftKey) {
          const lastSelectedRowId: string = Object.entries(rowSelection)
            .sort((a, b) => a[1][1] - b[1][1])
            .slice(-1)[0][0];
          const [start, end] = [+lastSelectedRowId, +row.id].sort();
          for (let i = start; i <= end; i += 1) {
            addSelection(i.toString(), true);
          }
        } else {
          setRowSelection({});
          addSelection(row.id, true);
        }
      },
      selected: rowSelection[row.id]?.[0],
      sx: {
        cursor: 'pointer',
      },
    }),
  });

  tableRef.current = table;

  useEffect(() => {
    const handleCopy = (event: KeyboardEvent) => {
      if (event.key === 'c' && (event.metaKey || event.ctrlKey)) {
        // Check if the user is currently focused on an input, textarea, or contenteditable element
        const activeElement = document.activeElement;
        const isEditingInput =
          activeElement &&
          (activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.getAttribute('contenteditable') === 'true' ||
            // Also check for elements with role="textbox" or if it's within a form field
            activeElement.getAttribute('role') === 'textbox' ||
            // Check if the activeElement is a child of an editable element
            (activeElement.closest &&
              (activeElement.closest('input') || activeElement.closest('textarea') || activeElement.closest('[contenteditable="true"]'))));

        // If user is editing an input field, don't trigger table copy functionality
        if (isEditingInput) {
          return;
        }

        const selectedCharges = Object.entries(rowSelection)
          .filter((row) => row[1][0])
          .map((row) => row[1][2]);

        // Only proceed if there are selected charges
        if (selectedCharges.length > 0) {
          setCopiedCharges(selectedCharges);
          // Use the selectedCharges.length instead of copiedCharges.length
          // so we get the correct count immediately
          openToast({
            toastType: 'success',
            title: 'Success',
            description: `${selectedCharges.length} rate card charge${selectedCharges.length !== 1 ? 's' : ''} copied!`,
          });
        } else {
          // Provide feedback when no rows are selected
          openToast({
            toastType: 'warning',
            title: 'No Selection',
            description: 'Please select at least one row to copy',
          });
        }
      }
    };
    const handlePaste = async (event: KeyboardEvent) => {
      if (event.key === 'v' && (event.metaKey || event.ctrlKey)) {
        // Check if the user is currently focused on an input, textarea, or contenteditable element
        const activeElement = document.activeElement;
        const isEditingInput =
          activeElement &&
          (activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.getAttribute('contenteditable') === 'true' ||
            // Also check for elements with role="textbox" or if it's within a form field
            activeElement.getAttribute('role') === 'textbox' ||
            // Check if the activeElement is a child of an editable element
            (activeElement.closest &&
              (activeElement.closest('input') || activeElement.closest('textarea') || activeElement.closest('[contenteditable="true"]'))));

        // If user is editing an input field, don't trigger table paste functionality
        if (isEditingInput) {
          return;
        }

        // Check if contractFlow exists (tables should be ready)
        if (!contractFlow?.id) {
          openToast({
            toastType: 'warning',
            title: 'Not Ready',
            description: 'Please select a contract flow before pasting charges',
          });
          return;
        }

        // Check if there's anything to paste
        if (copiedCharges.length === 0) {
          openToast({
            toastType: 'info',
            title: 'Nothing to Paste',
            description: 'Copy charges first with Cmd+C or Ctrl+C',
          });
          return;
        }

        try {
          await addRateCardCharges({
            contractFlowId: contractFlow.id,
            data: copiedCharges,
          });

          openToast({
            toastType: 'success',
            title: 'Success',
            description: `${copiedCharges.length} rate card charge${copiedCharges.length !== 1 ? 's' : ''} pasted!`,
          });
        } catch (error) {
          openToastError(new Error(`Failed to add the copied rate card charges`));
        }
      }
    };
    window.addEventListener('keydown', handleCopy);
    window.addEventListener('keydown', handlePaste);
    return () => {
      window.removeEventListener('keydown', handleCopy);
      window.removeEventListener('keydown', handlePaste);
    };
  }, [rowSelection, contractFlow, addRateCardCharges, copiedCharges, setCopiedCharges]);

  return (
    <div key={`table-${chargeCategory}`} className='h-full'>
      <NoRadiusTableContainer>
        <CustomTable
          table={table}
          tableContainerProps={{
            sx: {
              height: '100%',
              maxHeight: '100%',
              overflow: 'auto',
              '& .MuiTable-root': {
                borderCollapse: 'separate',
              },

              '& .MuiTableRow-head': {
                backgroundColor: '#ffffff',
              },
              // Scrollbar styling
              '&::-webkit-scrollbar': {
                height: '12px',
                width: '12px',
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#BDBDBD',
                borderRadius: '6px',
                border: '3px solid #f1f1f1',
                backgroundClip: 'content-box',
                '&:hover': {
                  backgroundColor: '#999999',
                },
              },
            },
          }}
        />
      </NoRadiusTableContainer>
    </div>
  );
};
